import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, getDocs, addDoc, serverTimestamp, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
    PlusIcon, BookOpenIcon, AcademicCapIcon, 
    TrashIcon, XIcon, LinkIcon, PencilIcon, 
    CheckCircleIcon, ExternalLinkIcon, SearchIcon,
    ExclamationIcon 
} from '../../components/icons/Icons';

// --- HELPERS ---

const getFinancialYear = (dateObj) => {
    if (!dateObj) return '';
    const date = new Date(dateObj);
    const year = date.getFullYear();
    const month = date.getMonth(); 
    if (month < 3) return `${year - 1}-${year}`;
    return `${year}-${year + 1}`;
};

const ensureAbsoluteUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
};

export default function SelfLearningPanel({ userProfile, appId }) {
    const [activities, setActivities] = useState([]);
    
    // Table State
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

    // Modals
    const [isLogModalOpen, setIsLogModalOpen] = useState(false);
    const [isProofModalOpen, setIsProofModalOpen] = useState(false);
    
    // Data passing
    const [editingItem, setEditingItem] = useState(null); 
    const [completingItem, setCompletingItem] = useState(null); 

    const fetchActivities = async () => {
        try {
            const q = query(
                collection(db, `artifacts/${appId}/pd_activities`),
                where('userId', '==', userProfile.id),
                where('type', 'in', ['Technical', 'Knowledge']), 
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);
            setActivities(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (error) {
            console.error("Error fetching self-learning:", error);
        }
    };

    useEffect(() => {
        fetchActivities();
    }, [appId]);

    // --- TABLE LOGIC ---
    const filteredActivities = useMemo(() => {
        return activities.filter(item => 
            item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.type.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [activities, searchTerm]);

    const sortedActivities = useMemo(() => {
        let sortableItems = [...filteredActivities];
        if (sortConfig.key) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];
                if (sortConfig.key === 'startDate' || sortConfig.key === 'createdAt') {
                    aValue = new Date(aValue || 0);
                    bValue = new Date(bValue || 0);
                }
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [filteredActivities, sortConfig]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = sortedActivities.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(sortedActivities.length / itemsPerPage);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Actions
    const handleDelete = async (id) => {
        if(window.confirm("Delete this request?")) {
            await deleteDoc(doc(db, `artifacts/${appId}/pd_activities`, id));
            fetchActivities();
        }
    };

    const openEdit = (item) => {
        setEditingItem(item);
        setIsLogModalOpen(true);
    };

    const openComplete = (item) => {
        setCompletingItem(item);
        setIsProofModalOpen(true);
    };

    return (
        <div className="space-y-6">
            
            {/* HEADER */}
            <div className="flex flex-col md:flex-row justify-between items-end border-b border-gray-100 pb-4 gap-4">
                <div>
                    <h3 className="text-lg font-bold text-[#213368]">Self-Learning & Skills</h3>
                    <p className="text-sm text-gray-500">Log your technical certifications and knowledge enhancement activities.</p>
                </div>
                <button 
                    onClick={() => { setEditingItem(null); setIsLogModalOpen(true); }}
                    className="flex items-center space-x-1 text-sm font-bold text-white bg-[#213368] hover:opacity-90 px-4 py-2 rounded-lg transition shadow-sm"
                >
                    <PlusIcon className="h-4 w-4" />
                    <span>Log New Activity</span>
                </button>
            </div>

            {/* SEARCH */}
            <div className="flex items-center bg-white p-2 rounded-lg border border-gray-200 shadow-sm w-full md:w-96">
                <SearchIcon className="h-5 w-5 text-gray-400 ml-2" />
                <input 
                    className="w-full p-2 text-sm outline-none"
                    placeholder="Search activity, provider or type..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} 
                />
            </div>

            {/* TABLE */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden min-h-[300px] flex flex-col">
                {activities.length === 0 ? (
                    <div className="flex-1 p-10 text-center flex flex-col items-center justify-center">
                        <div className="bg-gray-50 p-4 rounded-full mb-3">
                            <AcademicCapIcon className="h-8 w-8 text-gray-300" />
                        </div>
                        <p className="text-gray-500 font-medium">No learning activities logged yet.</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-left text-sm">
                                <thead className="bg-gray-50 text-gray-500 font-medium uppercase text-xs tracking-wider border-b border-gray-200">
                                    <tr>
                                        <SortableHeader label="Activity Details" sortKey="title" currentSort={sortConfig} onSort={handleSort} width="w-1/4" />
                                        <SortableHeader label="Type" sortKey="type" currentSort={sortConfig} onSort={handleSort} />
                                        <SortableHeader label="Dates" sortKey="startDate" currentSort={sortConfig} onSort={handleSort} />
                                        <SortableHeader label="Effort" sortKey="hours" currentSort={sortConfig} onSort={handleSort} align="right" />
                                        <SortableHeader label="Cost" sortKey="cost" currentSort={sortConfig} onSort={handleSort} />
                                        <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={handleSort} />
                                        <th className="px-6 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {currentItems.map((item) => (
                                        <tr key={item.id} className="hover:bg-gray-50 transition group">
                                            {/* DETAILS */}
                                            <td className="px-6 py-4">
                                                <div className="font-bold text-[#213368]">{item.title}</div>
                                                <div className="text-xs text-gray-500">{item.provider}</div>
                                                {item.link && (
                                                    <a href={ensureAbsoluteUrl(item.link)} target="_blank" rel="noreferrer" className="text-[10px] text-blue-500 hover:underline flex items-center mt-1">
                                                        <ExternalLinkIcon className="h-3 w-3 mr-1" /> View Course
                                                    </a>
                                                )}
                                                {/* Rejection Note */}
                                                {(item.status === 'Rejected' || item.status === 'Proof Rejected') && item.rejectionReason && (
                                                    <div className="mt-2 p-2 bg-red-50 border border-red-100 rounded text-xs text-red-700 flex items-start">
                                                        <ExclamationIcon className="h-4 w-4 mr-1 shrink-0" />
                                                        <span><strong>Note:</strong> {item.rejectionReason}</span>
                                                    </div>
                                                )}
                                            </td>
                                            
                                            {/* TYPE */}
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${item.type === 'Technical' ? 'bg-teal-50 text-teal-700' : 'bg-purple-50 text-purple-700'}`}>
                                                    {item.type}
                                                </span>
                                                {item.knowledgeType && <div className="text-[10px] text-gray-400 mt-1 uppercase tracking-wide">{item.knowledgeType}</div>}
                                            </td>

                                            {/* DATES */}
                                            <td className="px-6 py-4 text-gray-600 text-xs whitespace-nowrap">
                                                <div>{item.startDate ? new Date(item.startDate).toLocaleDateString() : 'TBD'}</div>
                                                <div className="text-gray-400 text-[10px] my-0.5">to</div>
                                                <div>{item.endDate ? new Date(item.endDate).toLocaleDateString() : 'TBD'}</div>
                                            </td>

                                            {/* EFFORT */}
                                            <td className="px-6 py-4 text-right">
                                                <span className="font-medium text-gray-700">{item.hours}</span> <span className="text-xs text-gray-400">Hrs</span>
                                            </td>

                                            {/* COST */}
                                            <td className="px-6 py-4 text-gray-600 font-mono text-xs">
                                                {item.cost > 0 ? `${item.currency} ${item.cost}` : 'Free'}
                                            </td>

                                            {/* STATUS */}
                                            <td className="px-6 py-4">
                                                <StatusBadge status={item.status} />
                                            </td>

                                            {/* ACTIONS */}
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end space-x-2">
                                                    
                                                    {/* 1. FIX REQUEST: If Rejected (Budget) or Pending */}
                                                    {(item.status === 'Pending Approval' || item.status === 'Rejected') && (
                                                        <button onClick={() => openEdit(item)} className="text-blue-500 hover:bg-blue-50 p-1.5 rounded" title={item.status === 'Rejected' ? "Fix Request & Resubmit" : "Edit Details"}>
                                                            <PencilIcon className="h-4 w-4" />
                                                        </button>
                                                    )}

                                                    {/* 2. FIX PROOF: If Proof Rejected */}
                                                    {item.status === 'Proof Rejected' && (
                                                        <button 
                                                            onClick={() => openComplete(item)} 
                                                            className="flex items-center text-xs font-bold bg-red-50 text-red-600 px-2 py-1.5 rounded hover:bg-red-100 border border-red-200"
                                                            title="Update Proof"
                                                        >
                                                            <PencilIcon className="h-3 w-3 mr-1" /> Fix Proof
                                                        </button>
                                                    )}

                                                    {/* 3. DELETE */}
                                                    {(item.status === 'Pending Approval' || item.status === 'Rejected') && (
                                                        <button onClick={() => handleDelete(item.id)} className="text-red-400 hover:bg-red-50 p-1.5 rounded" title="Delete">
                                                            <TrashIcon className="h-4 w-4" />
                                                        </button>
                                                    )}

                                                    {/* 4. MARK COMPLETE */}
                                                    {item.status === 'Approved' && (
                                                        <button 
                                                            onClick={() => openComplete(item)} 
                                                            className="flex items-center text-xs font-bold bg-green-50 text-green-600 px-3 py-1.5 rounded hover:bg-green-100 border border-green-200 transition"
                                                            title="Submit Proof"
                                                        >
                                                            <CheckCircleIcon className="h-3 w-3 mr-1" /> Mark Complete
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination Footer */}
                        {filteredActivities.length > itemsPerPage && (
                            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-between items-center">
                                <p className="text-xs text-gray-500">Page {currentPage} of {totalPages}</p>
                                <div className="flex space-x-2">
                                    <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 text-xs font-medium">Prev</button>
                                    <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="px-3 py-1 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 text-xs font-medium">Next</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* MODALS */}
            {isLogModalOpen && (
                <LogActivityModal 
                    appId={appId} userProfile={userProfile} preFill={editingItem}
                    onClose={() => setIsLogModalOpen(false)} 
                    onSuccess={() => { fetchActivities(); setIsLogModalOpen(false); }}
                />
            )}
            {isProofModalOpen && (
                <ProofCompletionModal 
                    appId={appId} item={completingItem}
                    onClose={() => setIsProofModalOpen(false)}
                    onSuccess={() => { fetchActivities(); setIsProofModalOpen(false); }}
                />
            )}
        </div>
    );
}

// --- SUB-COMPONENTS ---

function SortableHeader({ label, sortKey, currentSort, onSort, align = 'left', width }) {
    const isSorted = currentSort.key === sortKey;
    return (
        <th className={`px-6 py-3 cursor-pointer hover:text-[#20BEC6] transition select-none text-${align} ${width}`} onClick={() => onSort(sortKey)}>
            <span className={`${isSorted ? 'font-bold text-[#213368]' : ''}`}>{label}</span>
        </th>
    );
}

function StatusBadge({ status }) {
    const styles = {
        'Completed': 'bg-green-100 text-green-700 border border-green-200',
        'Approved': 'bg-blue-50 text-blue-700 border border-blue-200',
        'Pending Approval': 'bg-yellow-50 text-yellow-700 border border-yellow-200',
        'Pending Verification': 'bg-purple-50 text-purple-700 border border-purple-200', 
        'Rejected': 'bg-red-50 text-red-700 border border-red-200',
        'Proof Rejected': 'bg-red-50 text-red-700 border border-red-200' // Added Status
    };
    return (
        <span className={`inline-flex items-center px-2 py-1 rounded-md text-[10px] uppercase font-bold tracking-wide ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
            {status}
        </span>
    );
}

// ------------------------------------------------------------------
// MODAL 1: Log / Edit Activity
// ------------------------------------------------------------------
function LogActivityModal({ appId, userProfile, preFill, onClose, onSuccess }) {
    const isEdit = !!preFill?.id; 
    const [category, setCategory] = useState(preFill?.type || 'Technical');
    const [formData, setFormData] = useState({
        title: preFill?.title || '',
        provider: preFill?.provider || '',
        knowledgeType: preFill?.knowledgeType || 'Conference',
        startDate: preFill?.startDate ? new Date(preFill.startDate).toISOString().split('T')[0] : '',
        endDate: preFill?.endDate ? new Date(preFill.endDate).toISOString().split('T')[0] : '',
        hours: preFill?.hours || '',
        cost: preFill?.cost || '',
        currency: preFill?.currency || 'INR', 
        link: preFill?.link || ''
    });
    const [isSaving, setIsSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const payload = {
                userId: userProfile.id,
                userName: userProfile.name,
                managerId: userProfile.managerId || null,
                department: userProfile.department || null,
                type: category, 
                ...(category === 'Knowledge' && { knowledgeType: formData.knowledgeType }),
                title: formData.title,
                provider: formData.provider,
                startDate: new Date(formData.startDate).toISOString(), 
                endDate: new Date(formData.endDate).toISOString(),
                hours: Number(formData.hours),
                cost: Number(formData.cost),
                currency: formData.currency,
                link: formData.link,
                updatedAt: serverTimestamp()
            };

            if (isEdit) {
                // RESUBMIT LOGIC: If status was 'Rejected', reset to 'Pending Approval'
                if (preFill.status === 'Rejected') {
                    payload.status = 'Pending Approval';
                    payload.rejectionReason = null; 
                }
                await updateDoc(doc(db, `artifacts/${appId}/pd_activities`, preFill.id), payload);
            } else {
                payload.status = 'Pending Approval'; 
                payload.createdAt = serverTimestamp();
                await addDoc(collection(db, `artifacts/${appId}/pd_activities`), payload);
            }
            onSuccess();
        } catch (error) {
            console.error(error);
            alert("Error saving activity");
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-[#213368]">{isEdit ? 'Edit Request' : 'New Learning Request'}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XIcon className="h-6 w-6"/></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div onClick={() => setCategory('Technical')} className={`p-3 border rounded-lg cursor-pointer text-center transition ${category === 'Technical' ? 'border-[#20BEC6] bg-teal-50' : 'border-gray-200'}`}>
                            <AcademicCapIcon className={`h-6 w-6 mx-auto mb-1 ${category === 'Technical' ? 'text-[#20BEC6]' : 'text-gray-400'}`} />
                            <span className="text-xs font-bold">Technical Skill</span>
                        </div>
                        <div onClick={() => setCategory('Knowledge')} className={`p-3 border rounded-lg cursor-pointer text-center transition ${category === 'Knowledge' ? 'border-[#20BEC6] bg-teal-50' : 'border-gray-200'}`}>
                            <BookOpenIcon className={`h-6 w-6 mx-auto mb-1 ${category === 'Knowledge' ? 'text-[#20BEC6]' : 'text-gray-400'}`} />
                            <span className="text-xs font-bold">Knowledge Enh.</span>
                        </div>
                    </div>
                    {category === 'Knowledge' && (
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Activity Type</label>
                            <select className="w-full border border-gray-300 p-2 rounded-lg text-sm bg-white" value={formData.knowledgeType} onChange={e => setFormData({...formData, knowledgeType: e.target.value})}>
                                <option value="Conference">Conference / Summit</option>
                                <option value="Paper Publication">Paper Publication</option>
                                <option value="Market Visit">Market Visit</option>
                                <option value="Workshop">Workshop</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    )}
                    <div className="space-y-3">
                        <input className="w-full border border-gray-300 p-2.5 rounded-lg text-sm" placeholder="Activity Title" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required />
                        <input className="w-full border border-gray-300 p-2.5 rounded-lg text-sm" placeholder="Provider (e.g. Udemy)" value={formData.provider} onChange={e => setFormData({...formData, provider: e.target.value})} required />
                        <div className="flex space-x-2">
                             <input type="text" placeholder="Link (Optional)" className="w-full border border-gray-300 p-2.5 rounded-lg text-sm" value={formData.link} onChange={e => setFormData({...formData, link: e.target.value})} />
                        </div>
                    </div>
                    <div className="flex space-x-4">
                        <div className="w-1/2">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Cost</label>
                            <div className="flex">
                                <select className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-l-lg border-r-0 p-2.5 focus:outline-none" value={formData.currency} onChange={e => setFormData({...formData, currency: e.target.value})}>
                                    <option value="INR">INR</option>
                                    <option value="USD">USD</option>
                                    <option value="SGD">SGD</option>
                                </select>
                                <input type="number" className="w-full border border-gray-300 p-2.5 rounded-r-lg text-sm focus:outline-none" value={formData.cost} onChange={e => setFormData({...formData, cost: e.target.value})} placeholder="0.00" />
                            </div>
                        </div>
                        <div className="w-1/2">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Effort (Hrs)</label>
                            <input type="number" step="0.5" className="w-full border border-gray-300 p-2.5 rounded-lg text-sm" value={formData.hours} onChange={e => setFormData({...formData, hours: e.target.value})} required />
                        </div>
                    </div>
                    <div className="flex space-x-4">
                        <div className="flex-1"><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Start</label><input type="date" className="w-full border border-gray-300 p-2 rounded-lg text-sm" value={formData.startDate} onChange={e => setFormData({...formData, startDate: e.target.value})} required /></div>
                        <div className="flex-1"><label className="block text-xs font-bold text-gray-500 uppercase mb-1">End</label><input type="date" className="w-full border border-gray-300 p-2 rounded-lg text-sm" value={formData.endDate} onChange={e => setFormData({...formData, endDate: e.target.value})} required /></div>
                    </div>
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                        <button type="button" onClick={onClose} className="px-5 py-2 text-gray-600 font-medium">Cancel</button>
                        <button type="submit" disabled={isSaving} className="px-6 py-2 bg-[#20BEC6] text-white rounded-lg font-bold hover:opacity-90">{isSaving ? 'Saving...' : 'Submit Request'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ------------------------------------------------------------------
// MODAL 2: Proof of Completion
// ------------------------------------------------------------------
function ProofCompletionModal({ appId, item, onClose, onSuccess }) {
    // If it's a "Proof Rejected" situation, prefill with existing data
    const [proofLink, setProofLink] = useState(item.proofLink || item.link || '');
    // Default to existing completion date if it exists, else today
    const [completionDate, setCompletionDate] = useState(
        item.completedAt 
        ? new Date(item.completedAt.seconds * 1000).toISOString().split('T')[0] 
        : new Date().toISOString().split('T')[0]
    );
    const [isSaving, setIsSaving] = useState(false);

    const handleComplete = async (e) => {
        e.preventDefault();
        if (!proofLink) return alert("Please provide a proof link.");
        
        setIsSaving(true);
        try {
            const completedAtDate = new Date(completionDate);
            
            await updateDoc(doc(db, `artifacts/${appId}/pd_activities`, item.id), {
                // RESUBMIT LOGIC: Reset to 'Pending Verification'
                status: 'Pending Verification', 
                rejectionReason: null, // Clear any proof rejection reason
                
                proofLink: proofLink,
                date: completedAtDate, 
                fy: getFinancialYear(completedAtDate),
                completedAt: completedAtDate,
                submissionTimestamp: serverTimestamp()
            });
            onSuccess();
        } catch (error) {
            console.error(error);
            alert("Error submitting proof");
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
                <h3 className="text-lg font-bold text-[#213368] mb-2">Mark as Complete</h3>
                
                {/* Show rejection note if updating proof */}
                {item.status === 'Proof Rejected' && item.rejectionReason && (
                     <div className="mb-4 p-2 bg-red-50 border border-red-100 rounded text-xs text-red-700">
                        <strong>Correction Needed:</strong> {item.rejectionReason}
                    </div>
                )}
                
                <p className="text-sm text-gray-600 mb-4">
                    Please provide details for verification.
                </p>

                <form onSubmit={handleComplete}>
                    <div className="mb-4">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Actual Completion Date</label>
                        <input 
                            type="date"
                            className="w-full border border-gray-300 p-2.5 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#20BEC6]"
                            value={completionDate}
                            onChange={e => setCompletionDate(e.target.value)}
                            required
                        />
                        <p className="text-[10px] text-gray-400 mt-1">This determines the Financial Year for your scorecard.</p>
                    </div>

                    <div className="mb-4">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Certificate / Proof URL</label>
                        <div className="flex items-center border border-gray-300 rounded-lg p-2.5 bg-white focus-within:ring-2 focus-within:ring-[#20BEC6]">
                            <LinkIcon className="h-4 w-4 text-gray-400 mr-2" />
                            <input 
                                className="w-full outline-none text-sm"
                                placeholder="https://drive.google.com/..."
                                value={proofLink}
                                onChange={e => setProofLink(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 text-gray-500">Cancel</button>
                        <button type="submit" disabled={isSaving} className="px-6 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700">
                            {isSaving ? 'Sending...' : 'Send for Verification'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}